import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, Languages, Users, Check, Loader2, ArrowRight, Plus, Route } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useBusiness } from "@/context/BusinessContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const SECTORS = [
  { id: "real_estate", en: "Real Estate", ar: "عقارات", icon: "🏢" },
  { id: "clinic", en: "Clinic / Healthcare", ar: "عيادة / صحة", icon: "🏥" },
  { id: "retail", en: "Retail", ar: "تجزئة", icon: "🛍️" },
  { id: "logistics", en: "Logistics", ar: "خدمات لوجستية", icon: "🚚" },
  { id: "education", en: "Education", ar: "تعليم", icon: "🎓" },
  { id: "home_services", en: "Home Services", ar: "خدمات منزلية", icon: "🔧" },
  { id: "travel", en: "Travel", ar: "سفر", icon: "✈️" },
  { id: "general", en: "Other", ar: "أخرى", icon: "💼" },
];

const LANG_FLAG = { en: "🇬🇧", ar: "🇸🇦" };

export default function Onboarding() {
  const { t, lang } = useI18n();
  const { refresh } = useBusiness();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [sector, setSector] = useState("general");
  const [autoTranslate, setAutoTranslate] = useState(true);
  const [routing, setRouting] = useState(true);
  const [agents, setAgents] = useState([]);
  const [agentForm, setAgentForm] = useState({ name: "", email: "", password: "", proficient_languages: ["en"] });
  const [saving, setSaving] = useState(false);

  const toggleLang = (l) =>
    setAgentForm((a) => ({
      ...a,
      proficient_languages: a.proficient_languages.includes(l)
        ? a.proficient_languages.filter((x) => x !== l)
        : [...a.proficient_languages, l],
    }));

  const addAgent = async () => {
    if (!agentForm.name || !agentForm.email || agentForm.password.length < 6) {
      toast.error("Enter name, email and a 6+ char password");
      return;
    }
    try {
      await api.post("/team", { ...agentForm, role: "agent" });
      setAgents((a) => [...a, { ...agentForm }]);
      setAgentForm({ name: "", email: "", password: "", proficient_languages: ["en"] });
      toast.success("Agent invited");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add agent");
    }
  };

  const finish = async () => {
    setSaving(true);
    try {
      await api.post("/business/onboard", {
        sector,
        auto_translate_incoming: autoTranslate,
        agent_reply_lang: "auto",
        language_routing: routing,
      });
      await refresh();
      navigate("/inbox");
    } catch (e) {
      toast.error("Could not complete setup");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background grid place-items-center p-5">
      <div className="w-full max-w-2xl bg-white rounded-3xl border border-border shadow-sm p-8">
        <div className="flex items-center gap-2 mb-1">
          {[1, 2, 3].map((s) => (
            <div key={s} className={`h-1.5 flex-1 rounded-full ${s <= step ? "bg-accent" : "bg-secondary"}`} />
          ))}
        </div>
        <p className="text-sm text-muted-foreground mt-3">{t("onboardingStep")} {step}/3</p>
        <h1 className="font-head font-bold text-2xl sm:text-3xl text-foreground mt-1 mb-6">{t("onboardingTitle")}</h1>

        {step === 1 && (
          <div data-testid="onboard-step-sector">
            <Label className="text-base flex items-center gap-2 mb-4"><Building2 size={18} /> {t("chooseSector")}</Label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {SECTORS.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setSector(s.id)}
                  data-testid={`sector-${s.id}`}
                  className={`flex flex-col items-center gap-2 py-4 rounded-2xl border-2 transition-colors ${sector === s.id ? "border-accent bg-accent/5" : "border-border hover:border-accent/40"}`}
                >
                  <span className="text-2xl">{s.icon}</span>
                  <span className="text-xs font-medium text-foreground text-center px-1">{lang === "ar" ? s.ar : s.en}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 2 && (
          <div data-testid="onboard-step-lang" className="space-y-3">
            <Label className="text-base flex items-center gap-2 mb-1"><Languages size={18} /> {t("setLanguages")}</Label>
            <div className="flex items-center justify-between p-4 rounded-2xl border border-border">
              <div className="pe-4">
                <div className="font-medium text-foreground">{t("autoTranslateIncoming")}</div>
                <div className="text-sm text-muted-foreground mt-0.5">{t("autoTranslateHint")}</div>
              </div>
              <Switch checked={autoTranslate} onCheckedChange={setAutoTranslate} data-testid="onboard-auto-translate" />
            </div>
            <div className="flex items-center justify-between p-4 rounded-2xl border border-border">
              <div className="pe-4">
                <div className="font-medium text-foreground flex items-center gap-2"><Route size={16} /> {t("languageRouting")}</div>
                <div className="text-sm text-muted-foreground mt-0.5">{t("languageRoutingHint")}</div>
              </div>
              <Switch checked={routing} onCheckedChange={setRouting} data-testid="onboard-routing" />
            </div>
          </div>
        )}

        {step === 3 && (
          <div data-testid="onboard-step-team">
            <Label className="text-base flex items-center gap-2 mb-4"><Users size={18} /> {t("inviteTeam")}</Label>
            <div className="space-y-2 mb-3">
              {agents.map((a, i) => (
                <div key={i} className="flex items-center gap-2 p-2 rounded-lg bg-secondary text-sm" data-testid={`onboard-agent-row-${i}`}>
                  <Check size={15} className="text-accent" /> {a.name} · {a.email}
                  <span className="ms-auto flex items-center gap-1">
                    {(a.proficient_languages || []).map((l) => (
                      <span key={l} className="text-xs">{LANG_FLAG[l]}</span>
                    ))}
                  </span>
                </div>
              ))}
            </div>
            <div className="grid sm:grid-cols-3 gap-2">
              <Input placeholder={t("agentName")} value={agentForm.name} onChange={(e) => setAgentForm((f) => ({ ...f, name: e.target.value }))} data-testid="onboard-agent-name" />
              <Input placeholder={t("email")} value={agentForm.email} onChange={(e) => setAgentForm((f) => ({ ...f, email: e.target.value }))} data-testid="onboard-agent-email" />
              <Input placeholder={t("password")} type="password" value={agentForm.password} onChange={(e) => setAgentForm((f) => ({ ...f, password: e.target.value }))} data-testid="onboard-agent-password" />
            </div>
            <div className="flex items-center gap-2 text-sm mt-3">
              <span className="text-muted-foreground">{t("proficientLanguages")}:</span>
              {["en", "ar"].map((l) => {
                const on = agentForm.proficient_languages.includes(l);
                return (
                  <button key={l} type="button" data-testid={`onboard-agent-lang-${l}`}
                    onClick={() => toggleLang(l)}
                    className={`px-2.5 py-1 rounded-full text-xs font-semibold border transition-colors ${on ? "bg-primary/10 text-primary border-primary/30" : "bg-secondary text-muted-foreground border-transparent"}`}>
                    {LANG_FLAG[l]} {l.toUpperCase()}
                  </button>
                );
              })}
            </div>
            <Button variant="outline" onClick={addAgent} className="mt-3 rounded-xl" data-testid="onboard-add-agent">
              <Plus size={16} /> {t("inviteAgent")}
            </Button>
          </div>
        )}

        <div className="flex items-center justify-between mt-8">
          <Button variant="ghost" onClick={() => (step > 1 ? setStep(step - 1) : navigate("/inbox"))} data-testid="onboard-back">
            {step > 1 ? t("back") : t("skipForNow")}
          </Button>
          {step < 3 ? (
            <Button onClick={() => setStep(step + 1)} className="rounded-xl bg-accent hover:bg-accent/90" data-testid="onboard-next">
              {t("next")} <ArrowRight size={16} className="rtl:rotate-180" />
            </Button>
          ) : (
            <Button onClick={finish} disabled={saving} className="rounded-xl bg-accent hover:bg-accent/90" data-testid="onboard-finish">
              {saving ? <Loader2 className="animate-spin" /> : <>{t("finish")} <Check size={16} /></>}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
